import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { KeycloakPasswordAuthService } from './keycloak-password-auth.service';
import { PermissionsService } from '../permissions/permissions.service';
import { AppService } from '../app.service';
import { isMockAuthed } from './keycloak-auth.guard';

/**
 * API surface where a platform user acts ON BEHALF OF the agency selected in
 * the INVENTORY strip. Restricted to the search + booking funnel so the
 * selection re-tenants only inventory work — NETWORK/admin/customer screens
 * keep the caller's own context (and the platform-admin god-view). Matched
 * with {@code includes} to also catch direct-port dev calls.
 */
const INVENTORY_SURFACE = [
  '/api/v1/flights', '/api/v1/hotels', '/api/v1/trains', '/api/v1/buses',
  '/api/v1/tours', '/api/v1/ground', '/api/v1/cip', '/api/v1/visas',
  '/api/v1/insurance', '/api/v1/sim', '/api/v1/inventory', '/api/v1/pricing',
  '/api/v1/vouchers', '/api/v1/voucher-review',
];

/**
 * Mirrors the backend's `ota.security.dev-bypass=true` mode: when
 * `environment.devTenant.enabled` is true and the request doesn't already
 * carry a real bearer token, stamps the gateway-equivalent tenant headers
 * directly so the backend's `DevTenantFilter` can populate TenantContext
 * without Keycloak being in the loop.
 *
 * Order matters in `app.config.ts` — this interceptor MUST run AFTER
 * `keycloakBearerInterceptor` so it can detect whether bearer already
 * authenticated the request and skip itself in that case.
 *
 * Skips:
 *   - non-`/api/*` requests (static assets, third-party calls, etc.)
 *   - external, cross-origin `/api/*` calls that carry their own
 *     `Authorization` bearer (e.g. Tripall) — those must NOT receive our
 *     internal tenant headers.
 *
 * IMPORTANT — why we stamp even when a bearer is present:
 * In dev there is no api-gateway; the Angular proxy hits each service
 * directly. Services on `ota-security-starter` (supplier-service, etc.)
 * don't validate the JWT themselves — they trust the gateway to stamp
 * `X-Tenant-Agency`. So once the FE attaches a real Keycloak token, the
 * ONLY thing that can supply the tenant header for our own backend calls
 * is this interceptor. Bailing out on `Authorization` (as an earlier
 * version did) left every service with a missing-tenant 400. We therefore
 * still stamp for our own backend (relative `/api/*` or localhost) and
 * only skip for genuinely external hosts.
 *
 * The URL match uses {@code includes('/api/')} (not `startsWith`) so
 * cross-origin calls to services hit directly by IP/port — like the
 * dev-only {@code http://localhost:8128/api/v1/search-history} — get
 * the same dev headers stamped.
 *
 * Production builds must replace `environment.ts` with one that sets
 * `devTenant.enabled = false`. The interceptor still loads in prod but
 * becomes a pure passthrough.
 */
export const devTenantInterceptor: HttpInterceptorFn = (req, next) => {
  const cfg = environment.devTenant;
  if (!cfg?.enabled) return next(req);
  if (!req.url.includes('/api/')) return next(req);
  // Only external third-party calls that bring their own bearer are exempt —
  // our own backend still needs the dev tenant headers even with a token.
  if (req.headers.has('Authorization') && !isOwnBackendCall(req.url)) return next(req);

  // When a real Keycloak session is active, prefer the JWT's `sub` (a UUID)
  // as X-Authenticated-User. Backend's DevTenantFilter treats a UUID principal
  // as a signal to resolve the user's real permissions from identity-service
  // and stamp them as PERM_* authorities — so @PreAuthorize gates fire
  // correctly. A plain string principal (like the default 'dev-user') skips
  // that resolve path and leaves the caller with just ROLE_* authorities,
  // which trips every fine-grained permission check.
  const auth = inject(KeycloakPasswordAuthService, { optional: true });
  const profile = auth?.profile();
  const jwtSub = profile?.sub;
  const jwtName = profile?.name || profile?.preferredUsername;

  // Prefer the user's actual agency binding (from identity-service /
  // JWT claim) over the environment's mock default. Without this, every
  // request from every user stamps the same demo agencyId, and the BE
  // (SIGN resolver, tenant filters, etc.) operates against the wrong
  // tenant for anyone signed in as a real Keycloak user. Falls back to
  // the localStorage override, then the environment default, so no-auth
  // dev flows still work.
  //
  // SKIP for the /permissions endpoint itself — injecting PermissionsService
  // while its constructor's effect is firing its /permissions HTTP call
  // creates a circular dependency: interceptor → PermissionsService
  // → GET /permissions → interceptor → PermissionsService → … The URL
  // check makes this interceptor a no-op for the one call PermissionsService
  // makes, breaking the cycle. PermissionsService's fetchFromIdentity
  // doesn't need the tenant header anyway — identity-service scopes by
  // the sub in the URL path.
  const isPermissionsFetch = req.url.includes('/users/') && req.url.endsWith('/permissions');
  const perms = isPermissionsFetch ? null : inject(PermissionsService, { optional: true });
  const firstAgencyId = perms?.agencyIds()?.values()?.next()?.value;

  // Platform user acting on behalf of an agency for INVENTORY: the strip's
  // selection (AppService.inventoryAgency) drives the tenant for search +
  // booking calls, so pricing/markup and the created order belong to that
  // agency. Only applied on the inventory surface (see INVENTORY_SURFACE) —
  // elsewhere the caller keeps their own context. Null for agency users (the
  // strip never sets it) and before the first auto-select, so it's a no-op
  // then and firstAgencyId/the dev default still applies.
  const isInventoryCall =
    INVENTORY_SURFACE.some((p) => req.url.includes(p)) ||
    isInventoryOrderWrite(req);
  const app = isInventoryCall ? inject(AppService, { optional: true }) : null;
  const actingAgencyId = app?.inventoryAgency()?.id;
  const isPlatformAdmin = !!perms?.isPlatformAdmin();
  const shouldOmitDefaultAgencyHeader =
    isPlatformAdmin &&
    isOrderReadCall(req) &&
    !req.headers.has('X-Tenant-Agency');

  const overrides = readLocalStorageOverrides();
  const headers: Record<string, string> = {};
  // Caller-provided tenant headers win — a request that already carries one
  // (e.g. the tickets client acting as a platform admin) is left untouched;
  // we only fill in what's missing from the dev-tenant defaults.
  const put = (name: string, value: string | undefined) => {
    if (value != null && value !== '' && !req.headers.has(name)) headers[name] = value;
  };
  // Precedence: explicit dev override → inventory acting-agency (this call) →
  // the caller's own first agency → the dev-tenant default.
  if (!shouldOmitDefaultAgencyHeader) {
    put('X-Tenant-Agency', overrides.agencyId ?? actingAgencyId ?? firstAgencyId ?? cfg.agencyId);
  }
  // A localStorage override belongs only to the no-token dev session. Once a
  // real user is authenticated, never let a stale override impersonate that
  // user across logins (it would merge all chat histories into one identity).
  put('X-Authenticated-User', jwtSub ?? overrides.user ?? cfg.user);
  put('X-Authenticated-Name', jwtName ?? overrides.name ?? cfg.name);
  put('X-Tenant-Customer-Profile', overrides.customerProfileId ?? cfg.customerProfileId);
  put('X-Tenant-Staff-Profile', overrides.staffProfileId ?? cfg.staffProfileId);
  put('X-Dev-Roles', overrides.roles ?? cfg.roles);
  // Forward the caller's permission codes so the backend DevTenantFilter can
  // stamp PERM_* authorities and @PreAuthorize gates fire in dev-bypass. The
  // FE PermissionsService is the perm oracle (real login: fetched from
  // identity-service WITH the user's token; mock: the dev.tenant.perms seed).
  // Without this, perm-gated endpoints (e.g. content-service reference-data
  // admin) 403 in dev, because the backend's own identity permission-resolve
  // call is unauthenticated and gets nothing back. Prod (real bearer, gateway)
  // ignores this header.
  // With a real Keycloak login the backend resolves the user's permissions from
  // identity-service via the JWT `sub` (stamped above as X-Authenticated-User),
  // so forwarding the full perm list here is redundant. For a large perm set
  // (e.g. a platform super-admin with the whole taxonomy) the comma-joined list
  // pushes the request headers past Tomcat's 8KB cap → "Request header is too
  // large". Only stamp X-Dev-Perms in pure-mock dev (no real sub to resolve
  // from), or when an explicit localStorage override (dev.tenant.perms) is set.
  if (!jwtSub || overrides.perms) {
    const permCodes = perms ? Array.from(perms.permissions()) : [];
    put('X-Dev-Perms', overrides.perms ?? (permCodes.length ? permCodes.join(',') : undefined));
  }

  return Object.keys(headers).length ? next(req.clone({ setHeaders: headers })) : next(req);
};

/**
 * True when the request targets one of our own backend services — either a
 * relative `/api/*` path routed through the dev proxy, or an absolute
 * localhost URL hit directly by port. Everything else (absolute, non-local
 * origins like Tripall) is treated as external.
 */
function isOwnBackendCall(url: string): boolean {
  if (url.startsWith('/')) return true;
  return /^https?:\/\/(localhost|127\.0\.0\.1)(:|\/)/i.test(url);
}

/**
 * Only booking-funnel order WRITES should inherit the inventory strip's
 * acting-agency. Read paths like All Bookings (`GET /api/v1/orders`) and
 * simulator/reporting endpoints must stay in the caller's own context.
 */
function isInventoryOrderWrite(req: { url: string; method: string }): boolean {
  if (!req.url.includes('/api/v1/orders')) return false;
  if (req.method === 'GET') return false;
  if (req.url.includes('/api/v1/orders/simulate')) return false;
  if (req.url.includes('/api/v1/orders/sim-batch')) return false;
  return true;
}

function isOrderReadCall(req: { url: string; method: string }): boolean {
  if (req.method !== 'GET') return false;
  // Only the collection request is intentionally cross-agency for platform
  // users. Detail, attachment, voucher and PDF requests must retain the
  // selected/owning agency context.
  const path = req.url.split('?')[0].replace(/\/+$/, '');
  return path.endsWith('/api/v1/orders');
}

interface Overrides {
  agencyId?: string;
  customerProfileId?: string;
  staffProfileId?: string;
  user?: string;
  name?: string;
  roles?: string;
  perms?: string;
}

function readLocalStorageOverrides(): Overrides {
  if (typeof localStorage === 'undefined') return {};
  // These overrides are a dev/demo seam only. For a real authenticated session
  // they are actively harmful: a stale dev.tenant.agencyId can silently fence
  // every backend call to the wrong tenant. Keep them available for mock-auth
  // flows only.
  if (!isMockAuthed()) return {};
  try {
    return {
      agencyId: localStorage.getItem('dev.tenant.agencyId') ?? undefined,
      customerProfileId: localStorage.getItem('dev.tenant.customerProfileId') ?? undefined,
      staffProfileId: localStorage.getItem('dev.tenant.staffProfileId') ?? undefined,
      user: localStorage.getItem('dev.tenant.user') ?? undefined,
      name: localStorage.getItem('dev.tenant.name') ?? undefined,
      roles: localStorage.getItem('dev.tenant.roles') ?? undefined,
      perms: localStorage.getItem('dev.tenant.perms') ?? undefined,
    };
  } catch {
    return {};
  }
}
